import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  sendSignInLinkToEmail,
  linkWithCredential,
} from "firebase/auth";
import {
  shouldUseRedirect,
  pendingCredentialFromError,
  isProviderLinked,
  withoutEmailLinkParams,
  SIGN_IN_REDIRECT_PARAMS_KEY,
  postSignInDestination,
  SIGN_IN_REDIRECT_PROVIDER_KEY,
  SIGN_IN_REDIRECT_IN_PROGRESS_KEY,
} from "src/utils/firebaseSignIn.js";
import {
  firebaseAuthErrorMessage,
  firebaseLinkErrorMessage,
  SEND_LINK_FAILED_MESSAGE,
} from "src/utils/authErrorUtils.js";
import {
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  TextField,
  Link,
} from "@mui/material";
import { Google, Apple } from "@mui/icons-material";
import { auth } from "../firebase/firebase.js";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import Footer from "./blocks/footers/Footer.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import StanzaIcon from "src/assets/svg/stanza-small-icon.js";
import Microsoft from "src/assets/svg/microsoft.js";
import { capitalize, debounce } from "lodash";
import { getEmailValidationError } from "src/utils/Utils.js";
import { userAgentEmbeddedCheck } from "src/utils/userAgentEmbeddedCheck.js";
import Seo from "./Seo.js";
import { LoadingButton } from "./blocks/LoadingButton.js";
import { useMutation } from "@tanstack/react-query";
import { customEvent } from "src/utils/gtag.js"; // Import customEvent
import { rollbar } from "src/utils/ErrorBoundary/index.js";
import {
  emailDomainOf,
  isSignInCancel,
  reportSignInError,
  trackProviderSignInFailure,
} from "src/utils/signInReporting.js";
import SentLinkSuccessfully from "./blocks/dialogs/SentLinkSuccessfully.js";
import FeedbackForm from "./blocks/dialogs/FeedbackForm.js";

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");
const microsoftProvider = new OAuthProvider("microsoft.com");
const providers = {
  "google.com": { provider: googleProvider, label: "Google", name: "google", Icon: Google },
  "apple.com": { provider: appleProvider, label: "Apple", name: "apple", Icon: Apple },
  "microsoft.com": {
    provider: microsoftProvider,
    label: "Microsoft",
    name: "microsoft",
    Icon: Microsoft,
    iconStyle: { marginLeft: "4px" },
    buttonSx: { letterSpacing: "0.2px" },
  },
};

googleProvider.addScope("profile");
googleProvider.addScope("email");
appleProvider.addScope("email");

// Cadence for sampling document.hasFocus() to notice the OAuth popup opening
// (parent loses focus) and closing (parent regains it). Used instead of window
// blur/focus events, whose delivery races effect attachment and is flaky on a
// second popup open.
const POPUP_FOCUS_POLL_MS = 200;
const POPUP_CLOSE_GRACE_MS = 400; // let a successful sign-in win the focus race
// If the parent tab never loses focus this long after we start, no popup window
// ever opened ⇒ it was blocked. Firebase doesn't reliably reject with
// auth/popup-blocked on the first blocked attempt, so we detect it by focus and
// fall back to redirect ourselves rather than make the user click again (#2061).
const POPUP_BLOCKED_REDIRECT_MS = 1500;
// After we kick off the redirect fallback the page should navigate away. If it
// still hasn't (no pagehide) by now, the redirect was silently suppressed —
// treat it as a failure so the user isn't stranded on a dead screen.
const REDIRECT_WATCHDOG_MS = 4000;

// Firebase error codes where the popup couldn't complete for an environmental
// reason (popup blocked / cancelled, or the network dropped) — recoverable by
// retrying through the full-page redirect flow rather than failing the user.
const REDIRECT_FALLBACK_ERROR_CODES = [
  "auth/popup-blocked",
  "auth/cancelled-popup-request",
  "auth/network-request-failed",
];

/** Helper text for an address whose domain can't receive sign-in links. */
const BLOCKED_EMAIL_HELPER_TEXT =
  "We can't send sign-in links to this email provider. Try another address, or continue with Google, Apple or Microsoft.";

// A redirect account-exists rejection older than this is stale by the time a
// sign-in form mounts, so it isn't turned into a prompt.
const REDIRECT_ACCOUNT_EXISTS_MAX_AGE_MS = 60_000;

/**
 * Kick off the full-page redirect sign-in flow. The page navigates away and
 * UseAuth.checkSignIn resolves the result via getRedirectResult when the user
 * returns. Used both for the iOS/macOS path and as a fallback when a popup
 * sign-in is blocked or loses network mid-flight.
 *
 * @param {import("firebase/auth").AuthProvider} provider OAuth provider to use.
 * @returns {Promise<void>}
 */
const startRedirectSignIn = async (provider) => {
  sessionStorage.setItem(SIGN_IN_REDIRECT_PARAMS_KEY, window.location.search);
  sessionStorage.setItem(SIGN_IN_REDIRECT_PROVIDER_KEY, providerNameOf(provider));
  sessionStorage.setItem(SIGN_IN_REDIRECT_IN_PROGRESS_KEY, "1");
  await signInWithRedirect(auth, provider);
};

/**
 * Lowercased, trimmed email for comparison, or null when absent. Callers must
 * treat null as "unknown" — `null === null`, so it is not a match on its own.
 *
 * @param {string} [email]
 * @returns {string | null}
 */
const normalizeEmail = (email) =>
  typeof email === "string" && email.trim() ? email.trim().toLowerCase() : null;

/**
 * Short provider name for analytics, so every dimension uses one id format.
 *
 * @param {string} providerId Firebase provider id, e.g. "microsoft.com".
 * @returns {string}
 */
const providerNameFor = (providerId) =>
  providers[providerId]?.name ?? providerId ?? "";

/**
 * Short provider name for a Firebase `AuthProvider` instance.
 *
 * @param {import("firebase/auth").AuthProvider} provider
 * @returns {string}
 */
const providerNameOf = (provider) =>
  Object.values(providers).find((entry) => entry.provider === provider)?.name ??
  "";

/**
 * Shared sizing for provider buttons, so the prompt CTA and the list match.
 *
 * @param {boolean} dialog Rendered inside `SignInDialog`.
 * @returns {object} MUI `sx`.
 */
const providerButtonSx = (dialog) => ({
  width: "100%",
  fontSize: dialog ? { xs: "11.5px", md: "13px" } : "13px",
});

/**
 * Reports the outcome of a link attempt.
 *
 * @param {"linked" | "skipped" | "mismatched" | "failed" | "dropped"} outcome
 * @param {{ _id?: string, email?: string } | null} stanzaUser Signed-in Stanza
 *   user, or null when no session exists.
 * @param {string} providerName Provider the user just signed in with.
 * @param {string} pendingProviderId Provider id of the credential being linked.
 * @param {string} [errorCode] Firebase error code, or why the credential was dropped.
 */
const trackProviderLink = (
  outcome,
  stanzaUser,
  providerName,
  pendingProviderId,
  errorCode
) => {
  customEvent({
    name: `user_signin_provider_link_${outcome}`,
    category: "Authorization",
    label: `Provider Link ${capitalize(outcome)}`,
    data: {
      user_id: stanzaUser?._id ? String(stanzaUser._id) : "",
      user_email: stanzaUser?.email || "",
      provider: providerName,
      pending_provider: providerNameFor(pendingProviderId),
      error_code: errorCode || "",
      url: window.location.href,
    },
  });
};

/**
 * Reports a pending credential abandoned before any link was attempted.
 *
 * @param {{ cred: import("firebase/auth").OAuthCredential } | null} pending
 * @param {string} providerName
 * @param {string} reason
 */
const trackPendingCredentialDropped = (pending, providerName, reason) => {
  if (!pending?.cred) return;
  trackProviderLink("dropped", null, providerName, pending.cred.providerId, reason);
};

/**
 * Links a credential held over from a rejected sign-in onto the account the
 * user just authenticated with. Never throws.
 *
 * @param {import("firebase/auth").User} user Freshly signed-in Firebase user.
 * @param {{ cred: import("firebase/auth").OAuthCredential, email?: string }} pending
 *   Credential to link, and the account the prompt said it belonged to.
 * @returns {Promise<{ outcome: string | null, error?: { code?: string } }>}
 *   `outcome` is null when there was nothing to link.
 */
const linkPendingCredential = async (user, pending) => {
  const pendingCred = pending?.cred;
  if (!user || !pendingCred) return { outcome: null };

  // The account chooser lets the user pick a different account than the one the
  // prompt named; linking then welds the credential onto the wrong account.
  // Compare normalized (IdPs return their own casing), and refuse outright when
  // the target is unknown — two unknown addresses must not count as a match.
  const target = normalizeEmail(pending.email);
  if (!target || normalizeEmail(user.email) !== target) {
    return {
      outcome: "mismatched",
      error: { code: "stanza/link-account-mismatch" },
    };
  }

  if (isProviderLinked(user, pendingCred.providerId)) {
    return { outcome: "skipped" };
  }
  try {
    await linkWithCredential(user, pendingCred);
    return { outcome: "linked" };
  } catch (err) {
    // providerData is read from a cached token and can lag a fresh merge, so
    // this code still means "already linked", not a failure.
    if (err?.code === "auth/provider-already-linked") {
      return { outcome: "skipped", error: err };
    }
    rollbar.warning("[SignIn] linking pending credential failed", err);
    return { outcome: "failed", error: err };
  }
};

/**
 * Reports a failed `sendSignInLinkToEmail` to Rollbar and GA4 with the real
 * Firebase code. Logs the email domain and continue-URL shape, never the
 * address or param values.
 *
 * @param {{ code?: string, message?: string }} err
 * @param {string} email
 * @param {URL} continueUrl
 */
const reportSendLinkError = (err, email, continueUrl) => {
  const error = err?.code ?? err?.message;
  reportSignInError("sendSignInLinkToEmail", err, {
    emailDomain: emailDomainOf(email),
    continueOrigin: continueUrl.origin,
    continuePath: continueUrl.pathname,
    continueParamKeys: [...continueUrl.searchParams.keys()],
    continueUrlLength: continueUrl.href.length,
  });
  customEvent({
    name: "user_signin_send_link_error",
    category: "Authorization",
    label: "Send Sign-in Link Error",
    data: {
      provider: "email",
      error,
      url: window.location.href,
    },
  });
};

/**
 * @param {{ dialog?: boolean, headContent?: import("react").ReactNode }} props
 */
function SignIn({ dialog, headContent }) {
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sentLink, setSentLink] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  // Set when Firebase rejects a provider because the email already belongs to a
  // different one; drives the prompt that routes the user to that provider.
  const [existingAccountPrompt, setExistingAccountPrompt] = useState(null);
  // The pending link offer. Outlives the card: dismissing hides the prompt but a
  // later sign-in to the same account should still link.
  const pendingLinkRef = useRef(null);
  // Synchronous mirror of isSigningIn so rapid re-clicks on the same provider
  // are no-ops immune to stale closures (preserves #2013's double-click guard).
  const isSigningInRef = useRef(false);
  // Set once a popup sign-in actually succeeds, so the focus-based re-enable
  // below doesn't re-enable buttons mid-navigation on the happy path.
  const signedInRef = useRef(false);
  // Provider of the in-flight popup attempt, so the focus watcher can fall back
  // to redirect with the right provider if it detects the popup never opened.
  const pendingProviderRef = useRef(null);
  // Guards against initiating the redirect fallback twice (focus watcher + the
  // catch block can both detect the failure).
  const redirectingRef = useRef(false);
  // Redirect watchdog; cleared on unmount so it can't report a stale failure.
  const redirectWatchdogRef = useRef(null);

  const {
    user,
    checkSignIn,
    isCheckingRedirect,
    redirectAccountExistsError,
    clearRedirectAccountExistsError,
  } = useContext(AuthContext);

  /**
   * Fall back to the full-page redirect sign-in when the popup couldn't be used
   * (blocked or network-dropped). Idempotent for a single attempt. Self-contained:
   * never throws, and reports + recovers if the redirect itself fails so the user
   * isn't stranded with disabled buttons.
   *
   * @param {import("firebase/auth").AuthProvider} provider OAuth provider.
   * @param {string} reason Short tag describing how the popup failure was detected.
   * @param {string} [errorCode] Firebase error code, when one was surfaced.
   * @returns {Promise<void>}
   */
  const fallbackToRedirect = async (provider, reason, errorCode) => {
    if (redirectingRef.current || !provider) return;
    redirectingRef.current = true;
    // The redirect navigates away and the in-memory credential goes with it.
    trackPendingCredentialDropped(
      pendingCredFor(provider),
      providerNameOf(provider),
      reason
    );
    customEvent({
      name: "signin_popup_fallback_redirect",
      category: "Authorization",
      label: "Popup Failed - Redirect Fallback",
      data: {
        url: window.location.href,
        reason,
        error_code: errorCode || "",
      },
    });

    // pagehide fires when the redirect navigation actually commits; if it never
    // fires the redirect was silently suppressed (e.g. blocked by an extension).
    let navigationStarted = false;
    const markNavigating = () => {
      navigationStarted = true;
    };
    window.addEventListener("pagehide", markNavigating);

    /**
     * Report the redirect fallback failing and re-enable the buttons so the user
     * can retry or fall back to the email link.
     *
     * @param {string} failureReason How the redirect failure was detected.
     * @param {Error} [err] Underlying error, when the redirect threw.
     */
    const reportRedirectFailed = (failureReason, err) => {
      window.removeEventListener("pagehide", markNavigating);
      reportSignInError("redirectFallback", err, {
        provider: providerNameOf(provider),
        reason: failureReason,
        popupErrorCode: errorCode,
      });
      customEvent({
        name: "signin_redirect_fallback_failed",
        category: "Authorization",
        label: "Redirect Fallback Failed",
        data: {
          url: window.location.href,
          reason: failureReason,
          error_code: err?.code || errorCode || "",
          error: err?.message || "",
        },
      });
      redirectingRef.current = false;
      isSigningInRef.current = false;
      setIsSigningIn(false);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "We couldn't open the sign-in page. Please try again, or use the email link below.",
        "error"
      );
    };

    try {
      await startRedirectSignIn(provider);
      // The promise resolved but the page should be navigating away now. If it
      // hasn't by the watchdog, the redirect didn't take — report it.
      clearTimeout(redirectWatchdogRef.current);
      redirectWatchdogRef.current = setTimeout(() => {
        if (!navigationStarted) reportRedirectFailed("redirect_no_navigation");
      }, REDIRECT_WATCHDOG_MS);
    } catch (err) {
      reportRedirectFailed("redirect_threw", err);
    }
  };

  useEffect(() => () => clearTimeout(redirectWatchdogRef.current), []);

  useEffect(() => {
    if (!isSigningIn) return;

    // Watch the parent tab's focus to handle the two cases Firebase's promise
    // doesn't surface reliably:
    //  - The popup never opened (parent keeps focus) ⇒ it was blocked. Firebase
    //    often fails to reject auth/popup-blocked on the first blocked attempt,
    //    so we fall back to redirect ourselves instead of dead-ending the user.
    //  - The popup opened (parent lost focus) and then closed (focus returned)
    //    without a rejection — notably Apple / some Microsoft flows, #2061 — the
    //    promise hangs, so we re-enable the buttons. The grace delay lets a
    //    successful sign-in win the race: on success the parent also regains
    //    focus, but checkSignIn sets signedInRef (or navigation unmounts us)
    //    before the timer fires, so the buttons stay disabled through nav.
    let popupOpened = false;
    let elapsedMs = 0;
    let closeGraceTimer = null;

    const cancelCloseGrace = () => {
      if (closeGraceTimer != null) {
        clearTimeout(closeGraceTimer);
        closeGraceTimer = null;
      }
    };

    const interval = setInterval(() => {
      elapsedMs += POPUP_FOCUS_POLL_MS;

      if (!document.hasFocus()) {
        // Popup is in front of us (or the tab is hidden): it opened.
        popupOpened = true;
        cancelCloseGrace();
        return;
      }

      if (popupOpened) {
        // Popup opened and handed focus back ⇒ it closed.
        if (signedInRef.current) return;
        if (closeGraceTimer == null) {
          closeGraceTimer = setTimeout(() => {
            if (!signedInRef.current) {
              isSigningInRef.current = false;
              setIsSigningIn(false);
            }
          }, POPUP_CLOSE_GRACE_MS);
        }
        return;
      }

      // Parent never lost focus: no popup window opened ⇒ it was blocked.
      if (elapsedMs >= POPUP_BLOCKED_REDIRECT_MS && !redirectingRef.current) {
        fallbackToRedirect(
          pendingProviderRef.current,
          "popup_never_opened"
        );
      }
    }, POPUP_FOCUS_POLL_MS);

    return () => {
      clearInterval(interval);
      cancelCloseGrace();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSigningIn]);

  const clearSiginWithEmailTracking = () => {
    const newSearchParams = withoutEmailLinkParams(
      window.location.search,
      window.location.pathname
    );
    setSearchParams(newSearchParams);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${newSearchParams.toString()}`
    );
  };

  /**
   * The pending link offer, if it's waiting on this provider.
   *
   * @param {import("firebase/auth").AuthProvider} provider
   * @returns {{ cred: import("firebase/auth").OAuthCredential, email?: string, dismissed: boolean } | null}
   */
  const pendingCredFor = (provider) => {
    const offer = pendingLinkRef.current;
    if (!offer?.cred) return null;
    if (providers[offer.existingProviderId]?.provider !== provider) return null;
    return { cred: offer.cred, email: offer.email, dismissed: offer.dismissed };
  };

  /**
   * Shows the prompt routing the user to the provider their account already
   * uses, and arms the rejected credential to link on the way through.
   *
   * @param {import("firebase/auth").AuthError} err An account-exists rejection.
   * @param {import("firebase/auth").AuthProvider} provider Provider that was rejected.
   * @param {string} providerName
   * @param {{ linkable?: boolean }} [options] False when the credential can't
   *   survive until the next sign-in (it continues by redirect).
   * @returns {boolean} False when no other known provider could be offered.
   */
  const offerExistingAccount = (err, provider, providerName, { linkable = true } = {}) => {
    const email = err.customData?.email;
    const verifiedProvider = err.customData?._tokenResponse?.verifiedProvider;
    // Skip the provider that just failed: Firebase lists it among the
    // verified ones once it is linked, and pointing the user back at it
    // loops the same rejection forever.
    const existingProviderId = verifiedProvider?.find(
      (candidate) =>
        Object.prototype.hasOwnProperty.call(providers, candidate) &&
        providers[candidate].provider !== provider
    );
    if (!existingProviderId) return false;
    // Prompt instead of auto-opening a second popup: the popup must come
    // from a user gesture, and the credential is linked on the way through.
    const offer = {
      email,
      existingProviderId,
      attemptedProvider: providerName,
      pendingCred: linkable ? pendingCredentialFromError(err, provider) : null,
    };
    pendingLinkRef.current = {
      cred: offer.pendingCred,
      email,
      existingProviderId,
      attemptedProvider: providerName,
      dismissed: false,
    };
    setExistingAccountPrompt(offer);
    customEvent({
      name: "user_signin_link_prompt_shown",
      category: "Authorization",
      label: "Existing Account Prompt Shown",
      data: {
        provider: providerName,
        existing_provider: providerNameFor(existingProviderId),
        url: window.location.href,
      },
    });
    return true;
  };

  // A redirect sign-in rejected with account-exists comes back through UseAuth.
  useEffect(() => {
    const rejection = redirectAccountExistsError;
    if (!rejection || rejection.consumed) return;
    // Mutated so a second mounted form in the same commit doesn't prompt again.
    rejection.consumed = true;
    clearRedirectAccountExistsError();
    if (Date.now() - rejection.at > REDIRECT_ACCOUNT_EXISTS_MAX_AGE_MS) return;
    const attempted =
      providers[rejection.error.customData?._tokenResponse?.providerId];
    if (!attempted) return;
    // Redirect sign-ins continue by redirect, which drops an in-memory credential.
    const offered = offerExistingAccount(
      rejection.error,
      attempted.provider,
      attempted.name,
      { linkable: !shouldUseRedirect() }
    );
    // The prompt replaces UseAuth's generic snackbar for the same rejection.
    if (offered) setSnackbarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectAccountExistsError]);

  /**
   * Runs an OAuth sign-in for the given provider, linking any credential left
   * pending by an earlier rejected attempt on that same provider.
   *
   * @param {import("firebase/auth").AuthProvider} provider
   * @param {string} providerName
   * @returns {Promise<void>}
   */
  const signInWithProvider = async (provider, providerName) => {
    // Drop re-clicks while a popup sign-in is already in flight so we never race
    // concurrent signInWithPopup calls (the auth/cancelled-popup-request fix).
    if (isSigningInRef.current) return;
    isSigningInRef.current = true;
    signedInRef.current = false;
    redirectingRef.current = false;
    pendingProviderRef.current = provider;
    clearSiginWithEmailTracking(); // remove a possible old sign in with email tracking
    setIsSigningIn(true);
    let signedIn = false;
    try {
      customEvent({
        name: "user_click_signin",
        category: "Authorization",
        label: `Sign in with ${capitalize(providerName)} Click`,
        data: {
          provider: providerName,
        },
      });
      customEvent({
        name: `user_click_signin_${providerName}`,
        category: "Authorization",
        label: `Sign in with ${capitalize(providerName)} Click`,
        data: {
          url: window.location.href,
        },
      });

      if (shouldUseRedirect()) {
        // iOS / macOS: redirect to the OAuth provider instead of opening a popup.
        redirectingRef.current = true; // keep the focus watcher from racing us
        trackPendingCredentialDropped(pendingCredFor(provider), providerName, "redirect");
        await startRedirectSignIn(provider);
        return;
      }

      const pending = pendingCredFor(provider);
      const result = await signInWithPopup(auth, provider);
      // Mint the Stanza session first — linking is a nicety and must never
      // delay or block it.
      const signInResult = await checkSignIn(false, result);
      signedIn = !!signInResult;
      if (!signedIn) {
        // No session: don't mutate the Firebase account.
        trackPendingCredentialDropped(pending, providerName, "no_session");
        return;
      }
      signedInRef.current = true;

      const { outcome, error: linkError } = await linkPendingCredential(
        result.user,
        pending
      );
      // A mismatch after the user dismissed the offer is the expected result of
      // "use a different email", not an error.
      const abandoned = outcome === "mismatched" && pending?.dismissed;

      if (outcome) {
        // The Stanza `_id` arrives with the user load, after the session is
        // minted — the same point `user_signed_in` reads it.
        Promise.resolve(signInResult?.userData).then((stanzaUser) =>
          trackProviderLink(
            abandoned ? "dropped" : outcome,
            stanzaUser ?? null,
            providerName,
            pending.cred.providerId,
            abandoned ? "dismissed" : linkError?.code
          )
        );
      }
      if (!abandoned && (outcome === "mismatched" || outcome === "failed")) {
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          firebaseLinkErrorMessage(linkError),
          "warning"
        );
      }

      // Keep a live mismatch retryable: the user picked the wrong account and
      // can go again with the right one.
      if (!(outcome === "mismatched" && !pending?.dismissed)) {
        pendingLinkRef.current = null;
        setExistingAccountPrompt(null);
      }
    } catch (err) {
      console.log("signInWithProvider error", err);
      const cancelled = isSignInCancel(err);
      trackProviderSignInFailure(providerName, err);
      if (!cancelled) {
        reportSignInError("signInWithPopup", err, { provider: providerName });
      }
      if (err.code === "auth/account-exists-with-different-credential") {
        if (!offerExistingAccount(err, provider, providerName)) {
          showSnackBar(
            setSnackbarOpen,
            setSnackbarMessage,
            setSnackbarSeverity,
            firebaseAuthErrorMessage(err),
            "warning"
          );
        }
      } else if (!cancelled) {
        if (REDIRECT_FALLBACK_ERROR_CODES.includes(err.code)) {
          // The popup was blocked/cancelled or the network dropped mid-popup.
          // Retry through the full-page redirect flow, which doesn't depend on
          // popup permissions, instead of dead-ending the user. The focus watcher
          // may already have started this; fallbackToRedirect is idempotent.
          await fallbackToRedirect(provider, "firebase_error", err.code);
          return; // page is navigating away to the provider; nothing more to do
        }
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          firebaseAuthErrorMessage(err),
          "error"
        );
      }
    } finally {
      if (!signedIn) {
        isSigningInRef.current = false;
        setIsSigningIn(false);
      }
    }
  };

  /**
   * Signs in with the provider the account already uses, carrying the pending
   * credential from the rejected attempt so both end up on one account.
   *
   * @returns {Promise<void>}
   */
  const continueWithExistingProvider = async () => {
    if (!existingAccountPrompt || isSigningInRef.current) return;
    const { existingProviderId, attemptedProvider } = existingAccountPrompt;
    customEvent({
      name: "user_signin_link_prompt_clicked",
      category: "Authorization",
      label: "Existing Account Prompt Clicked",
      data: {
        provider: attemptedProvider,
        existing_provider: providerNameFor(existingProviderId),
        url: window.location.href,
      },
    });
    const { provider, name } = providers[existingProviderId];
    return signInWithProvider(provider, name);
  };

  /**
   * Hides the prompt. The offer stays armed so a later sign-in to the same
   * account still links; a different account is then treated as abandonment.
   */
  const dismissExistingAccountPrompt = () => {
    const offer = pendingLinkRef.current;
    if (offer) {
      offer.dismissed = true;
      customEvent({
        name: "user_signin_link_prompt_dismissed",
        category: "Authorization",
        label: "Existing Account Prompt Dismissed",
        data: {
          provider: offer.attemptedProvider,
          existing_provider: providerNameFor(offer.existingProviderId),
          url: window.location.href,
        },
      });
    }
    setExistingAccountPrompt(null);
  };

  /**
   * Click handler for a provider button.
   *
   * @param {string} providerId
   * @returns {() => Promise<void>}
   */
  const signInWithProviderId = (providerId) => {
    const { provider, name } = providers[providerId];
    return () => signInWithProvider(provider, name);
  };

  const isSubscribing = useMemo(() => {
    const forward = postSignInDestination(searchParams);
    if (forward) {
      return forward.includes("subscribing");
    } else if (searchParams.get("subscribing")) {
      return true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      // Dialogs leave the destination to their caller (popup) or to
      // RedirectSignInForward (redirect); only the sign-in page navigates.
      if (!dialog) {
        navigate(postSignInDestination(searchParams) || getPageRoute("home", "HOME"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { mutateAsync: handleSendEmail, isLoading } = useMutation(
    async ({ resend, email: address = email }) => {
      customEvent({
        name: "user_click_signin",
        category: "Authorization",
        label: "Sign in with Email Click",
        data: {
          provider: "email",
        },
      });
      customEvent({
        name: "user_click_signin_email",
        category: "Authorization",
        label: "Sign in with Email Click",
        data: {
          url: window.location.href,
        },
      });
      clearSiginWithEmailTracking(); // remove a possible old sign in with email tracking
      const continueUrl = new URL(
        window.location.pathname + window.location.search,
        window.location.origin
      );
      continueUrl.searchParams.set("email", address);
      try {
        await sendSignInLinkToEmail(auth, address, {
          url: continueUrl.href,
          handleCodeInApp: true,
        });
        setSentLink(true);
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          resend
            ? "Link resent, check your inbox."
            : "Link sent, check your inbox.",
          "success"
        );
      } catch (err) {
        console.error(err);
        reportSendLinkError(err, address, continueUrl);
        showSnackBar(
          setSnackbarOpen,
          setSnackbarMessage,
          setSnackbarSeverity,
          firebaseAuthErrorMessage(err, SEND_LINK_FAILED_MESSAGE),
          "error"
        );
      }
    }
  );

  const labels = {
    isSubscribing: {
      title: "Choose your Calendar",
      subtitle: "Events will be automatically synced.",
    },
    default: {
      title: "Sign in to Stanza",
      subtitle: "Calendars for everyone.",
    },
  }[isSubscribing ? "isSubscribing" : "default"];

  const mediumContainer = dialog
    ? {
        width: "auto",
        padding: 0,
        margin: "1rem 0 0 0",
        textAlign: "center",
      }
    : {
        margin: "auto",
        maxWidth: "400px",
        minWidth: "286px",
        textAlign: "center",
        p: 6,
      };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: dialog ? "auto" : "100vh",
      }}
    >
      <Box sx={mediumContainer}>
        {!sentLink ? (
          <SignInContent
            signInWithProviderId={signInWithProviderId}
            existingAccountPrompt={existingAccountPrompt}
            continueWithExistingProvider={continueWithExistingProvider}
            dismissExistingAccountPrompt={dismissExistingAccountPrompt}
            isSigningIn={isSigningIn || isCheckingRedirect}
            isLoading={isLoading}
            labels={labels}
            email={email}
            setEmail={setEmail}
            handleSendEmail={handleSendEmail}
            headContent={headContent}
            dialog={dialog}
          />
        ) : (
          <SentLinkSuccessfully
            email={email}
            isLoading={isLoading}
            handleSendEmail={handleSendEmail}
          />
        )}
        <Box sx={{ mt: 1 }}>
          <FeedbackForm
            category="signin-issues"
            title="What happened?"
            submitLabel="Send feedback"
            defaultEmail={email}
            options={[
              { key: "email-never-arrived", label: "Email never arrived" },
              { key: "link-didnt-work", label: "Email arrived but the link didn't work" },
              { key: "in-spam", label: "Email in spam or Promotions folder" },
              { key: "popup-blocked", label: "Sign-in popup was blocked" },
              { key: "provider-error", label: "Got an error using Google / Apple / Microsoft" },
              { key: "no-account", label: "Don't have any of these sign-in options" },
            ]}
            trigger={
              <Link component="span" variant="body2" sx={{ cursor: "pointer" }}>
                Having issues signing in?
              </Link>
            }
          />
        </Box>
      </Box>
      {!dialog && <Footer />}
    </div>
  );
}

/**
 * Prompt shown when the chosen provider's email already belongs to a different
 * provider, routing the user to that provider in one click.
 *
 * @param {{ prompt: object, onContinue: Function, onDismiss: Function, isSigningIn: boolean }} props
 */
const ExistingAccountPrompt = ({
  prompt,
  onContinue,
  onDismiss,
  isSigningIn,
  dialog,
}) => {
  const { email, existingProviderId, pendingCred } = prompt;
  const cardRef = useRef(null);
  const ctaRef = useRef(null);
  const explanationId = useId();

  // Focus the CTA, described by the explanation, so screen readers announce the
  // reason with it; a live region mounting with its content is often skipped.
  useEffect(() => {
    if (!dialog) cardRef.current?.scrollIntoView?.({ block: "nearest" });
    ctaRef.current?.focus?.();
  }, [dialog]);

  const {
    label: existingLabel,
    Icon: ProviderIcon,
    iconStyle,
    buttonSx,
  } = providers[existingProviderId] ?? {};
  const pendingLabel = providers[pendingCred?.providerId]?.label;

  return (
    <Box
      ref={cardRef}
      data-testid="existing-account-prompt"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        mb: 2.5,
        textAlign: "left",
      }}
    >
      <Typography
        id={explanationId}
        variant="body2"
        sx={{ textWrap: "pretty", overflowWrap: "anywhere", mb: 1.5 }}
      >
        {email ? <b>{email}</b> : "That email"} is already on Stanza with{" "}
        {existingLabel}. Continue with {existingLabel} to sign in
        {pendingLabel
          ? ` — we'll connect ${pendingLabel} to the same account.`
          : "."}
      </Typography>
      <Button
        ref={ctaRef}
        variant="contained"
        size="grown"
        data-testid="existing-account-prompt-continue"
        aria-describedby={explanationId}
        startIcon={ProviderIcon ? <ProviderIcon style={iconStyle} /> : null}
        sx={{ ...providerButtonSx(dialog), ...buttonSx }}
        onClick={onContinue}
        disabled={isSigningIn}
      >
        Continue with {existingLabel}
      </Button>
      <Box sx={{ textAlign: "center", mt: 1.5 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          data-testid="existing-account-prompt-dismiss"
          onClick={onDismiss}
          sx={{ cursor: "pointer" }}
        >
          Use a different email
        </Link>
      </Box>
    </Box>
  );
};

const SignInContent = ({
  signInWithProviderId,
  existingAccountPrompt,
  continueWithExistingProvider,
  dismissExistingAccountPrompt,
  isSigningIn,
  isLoading,
  labels,
  email,
  setEmail,
  handleSendEmail,
  headContent,
  dialog,
}) => {
  const [emailError, setEmailError] = useState(() =>
    getEmailValidationError(email)
  );
  const emailFieldRef = useRef(null);
  // Per instance: a shared debounce drops updates when two forms are mounted.
  const validateEmailDebounced = useMemo(
    () => debounce((value) => setEmailError(getEmailValidationError(value)), 300),
    []
  );

  useEffect(() => {
    // Enable as soon as the address is valid; only the error waits for a pause in typing.
    if (!getEmailValidationError(email)) {
      validateEmailDebounced.cancel();
      setEmailError(null);
    } else {
      validateEmailDebounced(email);
    }
  }, [email, validateEmailDebounced]);

  useEffect(() => () => validateEmailDebounced.cancel(), [validateEmailDebounced]);

  /**
   * Reads the field's DOM value, which autofill can set without an input event.
   *
   * @returns {string}
   */
  const syncEmailFromField = () => {
    const value = (emailFieldRef.current?.value ?? email).trim().toLowerCase();
    if (value !== email) setEmail(value);
    setEmailError(getEmailValidationError(value));
    return value;
  };

  /** @param {import("react").FormEvent} event */
  const handleSubmitEmail = (event) => {
    event.preventDefault();
    const value = syncEmailFromField();
    if (isLoading) return;
    if (getEmailValidationError(value)) {
      emailFieldRef.current?.focus();
      return;
    }
    handleSendEmail({ resend: false, email: value });
  };

  // An empty field stays submittable: autofill can fill it without an input
  // event, and submit reads the DOM value.
  const isSubmitDisabled =
    emailError === "blocked" || (Boolean(emailError) && email !== "");

  /**
   * Whether the prompt already offers this provider.
   *
   * @param {string} providerId
   * @returns {boolean}
   */
  const isOfferedByPrompt = (providerId) =>
    existingAccountPrompt?.existingProviderId === providerId;

  /** Hides the prompt and moves focus to the email field it points at. */
  const handleDismissPrompt = () => {
    dismissExistingAccountPrompt();
    emailFieldRef.current?.focus();
    emailFieldRef.current?.scrollIntoView?.({ block: "center" });
  };

  return (
    <>
      {!dialog && (
        <Seo
          title="Sign in — Stanza"
          description="Sign in to Stanza to create, discover and subscribe to calendars for sports, TV and entertainment, kept up to date on your phone."
        />
      )}
      {headContent ? (
        headContent
      ) : (
        <>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Stanza home"
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              lineHeight: 0,
            }}
          >
            <StanzaIcon style={{ width: 36, height: 40, cursor: "pointer" }} />
          </Box>
          <Typography variant={"h2"} sx={{ mt: 1 }}>
            {labels.title}
          </Typography>
          <Typography
            variant={"subtitle2"}
            sx={{ mb: 2 }}
            color="text.secondary"
          >
            {labels.subtitle}
          </Typography>
        </>
      )}
      {existingAccountPrompt && (
        <ExistingAccountPrompt
          key={`${existingAccountPrompt.existingProviderId}:${existingAccountPrompt.pendingCred?.providerId}`}
          dialog={dialog}
          prompt={existingAccountPrompt}
          onContinue={continueWithExistingProvider}
          onDismiss={handleDismissPrompt}
          isSigningIn={isSigningIn}
        />
      )}
      <Stack spacing={2.5}>
        {Object.entries(providers).map(
          ([providerId, { label, Icon, iconStyle, buttonSx }]) =>
            isOfferedByPrompt(providerId) ? null : (
              <Button
                key={providerId}
                variant="outlined"
                size="grown"
                data-testid={`signin-${providerNameFor(providerId)}`}
                startIcon={<Icon style={iconStyle} />}
                sx={{ ...providerButtonSx(dialog), ...buttonSx }}
                onClick={signInWithProviderId(providerId)}
                disabled={isSigningIn}
              >
                Continue with {label}
              </Button>
            )
        )}
      </Stack>
      <Divider sx={{ my: 2.5 }}>
        <Typography variant="subtitle2">Or with email</Typography>
      </Divider>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmitEmail}
        spacing={2.5}
        sx={{ pb: 2.5 }}
      >
        <TextField
          type="email"
          inputRef={emailFieldRef}
          data-testid="email-signin"
          value={email}
          label="Email Address"
          variant="standard"
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value.toLowerCase())}
          onBlur={syncEmailFromField}
          error={emailError === "blocked"}
          helperText={emailError === "blocked" ? BLOCKED_EMAIL_HELPER_TEXT : undefined}
        />
        <LoadingButton
          type="submit"
          variant="contained"
          size="grown"
          sx={{ width: "100%" }}
          loading={isLoading}
          disabled={isSubmitDisabled}
        >
          Send sign-in link
        </LoadingButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 1, fontSize: 14 }}
        >
          By continuing you agree to our{" "}
          <Link component={RouterLink} to={getPageRoute("terms", "TERMS")} target="_blank" rel="noopener">
            Terms
          </Link>{" "}
          and{" "}
          <Link component={RouterLink} to={getPageRoute("privacy", "PRIVACY")} target="_blank" rel="noopener">
            Privacy Policy
          </Link>
          .
        </Typography>
      </Stack>
    </>
  );
};

export default userAgentEmbeddedCheck(SignIn);
